import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Gift,
  Users,
  DollarSign,
  CheckCircle2,
  ArrowRight,
  Share2,
  Phone,
  Copy,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e8cdf70f-00bc-4528-a3bf-1c9ff1127e96/artifacts/zn03why2_lv_0_20260214074351.png";
const PROMO_FRONT = "https://customer-assets.emergentagent.com/job_gta-premium-clean/artifacts/4vk50mqi_cardpromo%20-%20Front.png";
const PROMO_BACK = "https://customer-assets.emergentagent.com/job_gta-premium-clean/artifacts/gklvxvyy_cardpromo%20-%20Back.png";

const steps = [
  {
    icon: Share2,
    title: "Share Your Code",
    description: "Give your unique referral code or share this page with friends and family"
  },
  {
    icon: Users,
    title: "Friend Books Cleaning",
    description: "Your friend books their first cleaning and mentions your name/code"
  },
  {
    icon: Gift,
    title: "Both Get Rewarded",
    description: "They get $25 off, you get $25 credit towards your next service!"
  }
];

export default function ReferPage() {
  const [referrerName, setReferrerName] = useState("");
  const [copied, setCopied] = useState(false);

  const referralLink = "www.torontopremiumcleaning.ca/refer";

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnWhatsApp = () => {
    const message = `Hey! I've been using Toronto Premium Cleaning and they're amazing! Use my referral and get $25 off your first cleaning. Book here: ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div data-testid="refer-page" className="bg-[#064E3B] min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        {/* Gold border decoration */}
        <div className="absolute inset-4 border-2 border-[#C0A062]/30 rounded-3xl pointer-events-none" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-6xl text-center relative z-10">
          {/* Logo */}
          <img
            src={LOGO_URL}
            alt="Toronto Premium Cleaning"
            className="h-20 md:h-24 w-auto mx-auto mb-8 brightness-0 invert"
          />
          
          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            <span className="text-[#C0A062]">Give $25</span>
            <span className="text-white">, </span>
            <span className="text-[#C0A062]">Get $25</span>
          </h1>
          
          <p className="text-2xl md:text-3xl text-white/90 font-medium italic mb-8">
            Share the shine with your neighbors and friends!
          </p>

          {/* Value Proposition */}
          <div className="bg-white/10 backdrop-blur-sm border border-[#C0A062]/50 rounded-2xl p-8 max-w-2xl mx-auto mb-12">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="text-center">
                <p className="text-[#C0A062] text-lg font-semibold mb-1">Your Friend Gets</p>
                <p className="text-4xl font-bold text-white">$25 OFF</p>
                <p className="text-white/70 text-sm">their first cleaning</p>
              </div>
              <div className="hidden md:block w-px h-20 bg-[#C0A062]/50" />
              <div className="text-center">
                <p className="text-[#C0A062] text-lg font-semibold mb-1">You Get</p>
                <p className="text-4xl font-bold text-white">$25 CREDIT</p>
                <p className="text-white/70 text-sm">towards your next service</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={shareOnWhatsApp}
              className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg"
              data-testid="share-whatsapp"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 mr-2 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Share on WhatsApp
            </Button>
            <Button
              onClick={copyToClipboard}
              className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg"
              data-testid="copy-link"
            >
              <Copy className="w-5 h-5 mr-2" />
              {copied ? "Copied!" : "Copy Link"}
            </Button>
          </div>

          <p className="text-white/60 text-sm">
            {referralLink}
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0C0A09] mb-4">
              How It Works
            </h2>
            <div className="w-20 h-1 mx-auto bg-gradient-to-r from-[#064E3B] to-[#C0A062]" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center" data-testid={`step-${index}`}>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#064E3B] flex items-center justify-center relative">
                  <step.icon className="w-10 h-10 text-[#C0A062]" />
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-[#C0A062] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-[#0C0A09] mb-2">
                  {step.title}
                </h3>
                <p className="text-[#78716C]">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promo Cards Display */}
      <section className="py-16 bg-[#FAFAF9]">
        <div className="container mx-auto px-4 md:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#0C0A09] mb-4">
              Share the Card
            </h2>
            <p className="text-[#78716C] max-w-2xl mx-auto">
              Give your friends a referral card or share this page. When they book their first cleaning and mention your name, you both save!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img src={PROMO_FRONT} alt="Referral Card Front" className="w-full" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
              <img src={PROMO_BACK} alt="Referral Card Back" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Referral Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">
          <Card className="border-2 border-[#064E3B]/20 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-[#C0A062]" />
                <h2 className="text-2xl font-semibold text-[#0C0A09] mb-2">
                  Claim Your Discount
                </h2>
                <p className="text-[#78716C]">
                  Were you referred by a friend? Enter their name below when you book!
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="referrer">Who referred you? (Name or Code)</Label>
                  <Input
                    id="referrer"
                    value={referrerName}
                    onChange={(e) => setReferrerName(e.target.value)}
                    placeholder="Enter your friend's name or referral code"
                    className="h-12 bg-[#FAFAF9] border-[#E7E5E4]"
                    data-testid="referrer-input"
                  />
                </div>

                <Link to="/booking" className="block">
                  <Button 
                    className="w-full bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full py-6 text-lg font-medium"
                    data-testid="book-now-refer"
                  >
                    Book Now & Save $25
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                <p className="text-center text-sm text-[#78716C]">
                  Mention your referrer's name when booking to receive your discount
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Terms & Conditions */}
      <section className="py-12 bg-[#FAFAF9] border-t border-[#E7E5E4]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h3 className="text-lg font-semibold text-[#0C0A09] mb-4 text-center">
            Program Terms
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#78716C]">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#064E3B] flex-shrink-0 mt-0.5" />
              <p>New client must book their first cleaning service</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#064E3B] flex-shrink-0 mt-0.5" />
              <p>$25 credit applied after referred cleaning is completed</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#064E3B] flex-shrink-0 mt-0.5" />
              <p>No limit on number of referrals - keep sharing!</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#064E3B] flex-shrink-0 mt-0.5" />
              <p>Credits can be combined with regular promotions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">
            Questions About the Program?
          </h2>
          <p className="text-white/80 mb-8">
            Contact us anytime - we're happy to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+16478700445">
              <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium">
                <Phone className="w-5 h-5 mr-2" />
                (647) 870-0445
              </Button>
            </a>
            <a href="https://wa.me/16478700445" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-[#064E3B] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium">
                Message on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
